import { useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { getSessionUser, getStudentRecordForSession, saveStudentEditRequest } from "@/lib/eisStore";

const FIELDS = [
  { key: "studentName", label: "Student Name" },
  { key: "motherName", label: "Mother's Name" },
  { key: "fatherName", label: "Father's Name" },
  { key: "standard", label: "Standard" },
  { key: "division", label: "Division" },
  { key: "phone", label: "Phone Number" },
  { key: "dob", label: "Date of Birth" },
  { key: "email", label: "Email" },
  { key: "bloodGroup", label: "Blood Group" },
  { key: "emergencyContact", label: "Emergency Contact" },
];

export default function ChangeOfInformation() {
  const user = getSessionUser() || {};
  const studentRecord = getStudentRecordForSession(user);

  const [field, setField] = useState("");
  const [oldValue, setOldValue] = useState("");
  const [newValue, setNewValue] = useState("");
  const [submitted, setSubmitted] = useState(false);

  if (user.role !== "student") {
    return (
      <AppShell>
        <div data-testid="change-of-information-page" className="space-y-6">
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <h2 className="text-2xl font-bold text-slate-900">Student edits only</h2>
              <p className="text-slate-500">
                Admins and staff should update student records from the Student Data page.
              </p>
              <Button
                type="button"
                onClick={() => (window.location.href = "/student-data")}
                className="rounded-full text-white"
                style={{ backgroundColor: "#0b1f7a" }}
              >
                Go to Student Data
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  const currentValue =
    field && studentRecord?.[field] !== undefined && studentRecord?.[field] !== ""
      ? studentRecord[field]
      : field && user[field] !== undefined && user[field] !== ""
        ? user[field]
        : "";

  const onFieldChange = (v) => {
    setField(v);
    setOldValue(studentRecord?.[v] || user[v] || "");
    setNewValue("");
    setSubmitted(false);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!field) {
      toast.error("Please choose a field");
      return;
    }
    if (!oldValue.trim() || !newValue.trim()) {
      toast.error("Please fill in both old and new values");
      return;
    }
      saveStudentEditRequest({
        studentId: studentRecord?.id || user.id || user.email,
        studentName: studentRecord?.studentName || user.studentName || user.name || "Student",
        standard: studentRecord?.standard || user.standard || "",
        division: studentRecord?.division || user.division || "",
        field,
        oldValue,
        newValue,
      });
    setSubmitted(true);
    toast.success("Request has been sent, the edits will be made soon");
  };

  return (
    <AppShell>
      <div data-testid="change-of-information-page" className="space-y-6">
        <div>
          <h2
            className="text-2xl md:text-3xl font-bold"
            style={{ color: "#0b1f7a" }}
          >
            Change of Information
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Pick a field, share the current value and the new value. The admin
            will review and update your record.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle style={{ color: "#0b1f7a" }}>New Request</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={submit}
              className="space-y-4 max-w-lg"
              data-testid="coi-form"
            >
              <div>
                <Label>Field to Change</Label>
                <Select value={field} onValueChange={onFieldChange}>
                  <SelectTrigger
                    className="mt-1"
                    data-testid="coi-field-select"
                  >
                    <SelectValue placeholder="Choose a field..." />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELDS.map((f) => (
                      <SelectItem
                        key={f.key}
                        value={f.key}
                        data-testid={`coi-field-option-${f.key}`}
                      >
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field && currentValue && (
                  <p className="text-xs text-slate-500 mt-1">
                    Current value on record:{" "}
                    <span className="font-semibold text-slate-700">
                      {currentValue}
                    </span>
                  </p>
                )}
              </div>

              <div>
                <Label>Old Value</Label>
                <Input
                  data-testid="coi-old-value"
                  value={oldValue}
                  onChange={(e) => setOldValue(e.target.value)}
                  placeholder="Enter current value"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>New Value</Label>
                <Input
                  data-testid="coi-new-value"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="Enter new value"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                data-testid="coi-submit-btn"
                className="w-full md:w-auto h-11 rounded-full text-white font-semibold px-8"
                style={{ backgroundColor: "#0b1f7a" }}
              >
                Change
              </Button>
            </form>

            {submitted && (
              <div
                data-testid="coi-success"
                className="mt-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 flex items-start gap-3"
              >
                <CheckCircle2 size={22} className="flex-shrink-0 mt-0.5" />
                <p className="text-sm md:text-base font-medium">
                  Request has been sent, the edits will be made soon
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
